import { Instagram, Facebook, MapPin } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang } from "../LanguageContext";
import { CONTACT, LOGO } from "../content";
import { scrollToId } from "../scroll";

export const Footer = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goSection = (id) => {
    if (pathname !== "/") navigate("/", { state: { scrollTo: id } });
    else scrollToId(id);
  };

  const links = [
    { id: "home", label: t.nav.home, action: () => goSection("home") },
    { id: "menu", label: t.nav.menu, action: () => goSection("featured") },
    { id: "gallery", label: t.nav.gallery, action: () => goSection("gallery") },
    { id: "reserve", label: t.nav.reserve, action: () => goSection("reserve") },
    { id: "contact", label: t.nav.contact, action: () => goSection("contact") },
  ];

  return (
    <footer className="bg-brown text-ivory pt-20 pb-10" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-16 border-b border-ivory/10">
          <div className="md:col-span-6">
            <div className="inline-flex bg-ivory rounded-2xl px-5 py-4 mb-6">
              <img src={LOGO} alt="Restaurante São João logo" className="h-16 w-auto" />
            </div>
            <p className="text-gold text-xs uppercase tracking-[0.35em] mb-4">Comporta · Portugal</p>
            <p className="font-serif text-ivory text-lg max-w-sm font-light">{t.footer.tagline}</p>
          </div>
          <div className="md:col-span-3">
            <p className="text-ivory/50 text-xs uppercase tracking-[0.25em] mb-5">{t.footer.quick}</p>
            <ul className="space-y-3">
              {links.map((l) => (
                <li key={l.id}>
                  <button onClick={l.action} className="text-ivory/80 hover:text-gold transition-colors text-sm">{l.label}</button>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="text-ivory/50 text-xs uppercase tracking-[0.25em] mb-5">{t.nav.contact}</p>
            <p className="text-ivory/80 text-sm font-light leading-relaxed mb-3">{CONTACT.address}</p>
            <a href={CONTACT.phoneHref} className="text-ivory/80 hover:text-gold transition-colors text-sm block mb-1" data-testid="footer-phone">{CONTACT.phone}</a>
            <a href={CONTACT.phone2Href} className="text-ivory/80 hover:text-gold transition-colors text-sm block mb-5" data-testid="footer-phone-2">{CONTACT.phone2}</a>
            <div className="flex items-center gap-4">
              <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-ivory/70 hover:text-gold transition-colors" data-testid="footer-instagram"><Instagram size={20} strokeWidth={1.5} /></a>
              <a href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-ivory/70 hover:text-gold transition-colors" data-testid="footer-facebook"><Facebook size={20} strokeWidth={1.5} /></a>
              <a href={CONTACT.maps} target="_blank" rel="noopener noreferrer" aria-label="Google Maps" className="text-ivory/70 hover:text-gold transition-colors" data-testid="footer-maps"><MapPin size={20} strokeWidth={1.5} /></a>
            </div>
          </div>
        </div>

        <div className="pt-10 overflow-hidden">
          <h2 className="font-serif text-ivory/95 leading-none tracking-tight text-[15vw] md:text-[11vw] whitespace-nowrap">São João</h2>
        </div>

        <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-ivory/40 text-xs">
          <p>© {new Date().getFullYear()} {CONTACT.name}. {t.footer.rights}</p>
          <p data-testid="footer-credit">
            {t.footer.creditPrefix}{" "}
            <a
              href="https://savrostudio.pt"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ivory/60 hover:text-gold transition-colors underline underline-offset-2"
              data-testid="savro-link"
            >
              SAVRO Studio
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
